import React, { useState } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import AudienceHero from '../../components/AudienceHero';
import HowItWorks from '../../components/HowItWorks';
import ClubsBand from '../../components/ClubsBand';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import FinalCTABanner from '../../components/FinalCTABanner';
import PrivacyPolicy from '../../components/PrivacyPolicy';
import { CALENDLY_URL, LOGO_URL, INSTAGRAM_URL } from '../../lib/siteConfig';

/**
 * /schools — dedicated landing for athletic directors, school programs,
 * and club / travel organizations evaluating program-wide mental performance.
 */
export default function SchoolsPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="schools-page">
      <Navbar calendlyLink={CALENDLY_URL} logoUrl={LOGO_URL} />
      <main>
        <AudienceHero
          testIdPrefix="schools-hero"
          eyebrow="For Schools & Clubs"
          headline="Program-wide mental performance training that doesn’t require a sport psychology budget."
          subhead="For athletic directors, school administrators, and club leadership building real mental skills across an entire roster — not just the captains. Workshops, consultation, and turnkey curriculum mapped to your season calendar."
          primaryCta={{ href: CALENDLY_URL, label: 'Request a Program Consultation', external: true }}
          secondaryCta={{ href: '/method', label: 'See the Apex Six Method' }}
        />

        <section
          className="py-16 sm:py-20"
          data-testid="schools-year-long-program"
          aria-labelledby="schools-year-long-heading"
        >
          <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
            <h2
              id="schools-year-long-heading"
              className="font-heading text-[1.9rem] sm:text-[2.4rem] font-bold tracking-tight leading-tight text-[#0a0d0e]"
              data-testid="schools-year-long-heading"
            >
              The year-long program
            </h2>
            <p className="mt-4 text-[15px] sm:text-[16px] text-[#3a3a3a] leading-relaxed max-w-[58ch]" data-testid="schools-year-long-subheading">
              Apex Six, sequenced across your school year - delivered by your own coaches.
            </p>
            <p className="mt-4 text-[15px] sm:text-[16px] text-[#3a3a3a] leading-relaxed max-w-[58ch]" data-testid="schools-year-long-intro">
              Most mental performance work in schools is a guest assembly. This is a full-year program, built into the school day, with your own staff trained to run it.
            </p>
            <ul className="mt-6 space-y-2.5" data-testid="schools-year-long-parts">
              {[
                'Full-year curriculum - the six Apex Six tools introduced in sequence through the year, then applied under real competitive pressure, mapped to your school calendar.',
                'Coach certification - your coaches are trained and certified to run every session, so delivery does not depend on an outside visitor and the capability stays in your building.',
                'Apex-led touchpoints - the launch session, the phase launches and the re-score debriefs, run by Coach Lewis.',
                'Parent education - the Car-Ride-Home Playbook and parent sessions, because most of the mental game happens away from the field.',
                'Built-in measurement - athletes score two baseline questions at the start, re-score them at day 14, and the year closes against a stated exit standard. No vague claims about confidence.',
              ].map((point, i) => (
                <li className="flex items-start gap-3 text-[14.5px] leading-[1.55] text-[#3a3a3a]" key={point} data-testid={`schools-year-long-part-${i + 1}`}>
                  <CheckCircle2 className="h-[18px] w-[18px] text-[#4FBCC7] shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="flex-1">{point}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[15px] sm:text-[16px] text-[#3a3a3a] leading-relaxed max-w-[58ch]" data-testid="schools-year-long-grades">
              Written for grades 6-8 and adaptable for high school programs, and designed to run inside the existing school day.
            </p>
            <p className="mt-4 text-[15px] sm:text-[16px] text-[#3a3a3a] leading-relaxed max-w-[58ch]" data-testid="schools-year-long-districts">
              For districts and multi-site programs, we run the same program across multiple schools with a single point of contact, and can align reporting to your board or procurement process.
            </p>
            <div className="mt-7">
              <Button
                asChild
                className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold h-12 px-7 rounded-xl"
              >
                <a href={CALENDLY_URL} data-testid="schools-year-long-planning-call">
                  Book a Planning Call
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* How it works for schools & clubs — 3-step program path */}
        <HowItWorks variant="school" />

        <ClubsBand calendlyLink={CALENDLY_URL} />
        <section
          id="pricing"
          className="py-20 sm:py-24 bg-white"
          data-testid="schools-program-engagement"
          aria-labelledby="schools-engagement-heading"
        >
          <div className="max-w-[1200px] mx-auto px-5 sm:px-6">
            <h2
              id="schools-engagement-heading"
              className="font-heading text-[2rem] sm:text-[2.5rem] font-bold tracking-tight text-[#0a0d0e] mb-4"
              data-testid="schools-engagement-heading"
            >
              How programs engage Apex
            </h2>
            <p className="mt-4 text-[15px] sm:text-[16px] text-[#3a3a3a] leading-relaxed max-w-[58ch]" data-testid="schools-engagement-intro">
              Apex programs are scoped to your roster, your season calendar and your coaching staff. Every engagement is built from the same four parts:
            </p>
            <ul className="mt-6 space-y-2.5" data-testid="schools-engagement-parts">
              {[
                'Year-long curriculum — Apex Six tools sequenced across your school year, mapped to your calendar.',
                'Coach certification — your coaches learn to run the sessions themselves, so delivery does not depend on an outside visitor.',
                'Apex-led touchpoints — the launch session, four phase launches, re-score debriefs and parent nights, run by Coach Lewis.',
                'Parent education — the Car-Ride-Home Playbook and parent sessions, because most of the mental game happens off the field.',
              ].map((point, i) => (
                <li className="flex items-start gap-3 text-[14.5px] leading-[1.55] text-[#3a3a3a]" key={point} data-testid={`schools-engagement-part-${i + 1}`}>
                  <CheckCircle2 className="h-[18px] w-[18px] text-[#4FBCC7] shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="flex-1">{point}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[15px] sm:text-[16px] text-[#3a3a3a] leading-relaxed max-w-[58ch]" data-testid="schools-engagement-pricing">
              Programs are priced per site based on roster size, so the cost is predictable across a school year and scales with your enrolment rather than with how many sessions you book.
            </p>
            <p className="mt-4 text-[15px] sm:text-[16px] text-[#3a3a3a] leading-relaxed max-w-[58ch]" data-testid="schools-engagement-proposal">
              <a href={CALENDLY_URL} className="text-[#004C54] font-medium hover:underline" data-testid="schools-engagement-planning-call">Book a Planning Call</a> for a proposal built around your program.
            </p>
          </div>
        </section>

        <FinalCTABanner
          eyebrow="Planning Call"
          headline="Let’s scope your program."
          subhead="A free 30-minute Planning Call covers roster size, season calendar, athletic director priorities, and budget realities. You leave with a concrete proposal you can put in front of your AD or board."
          primaryHref={CALENDLY_URL}
          primaryLabel="Book a Planning Call"
          secondaryHref="/method"
          secondaryLabel="Read the Method"
        />
      </main>
      <Footer
        calendlyLink={CALENDLY_URL}
        instagramLink={INSTAGRAM_URL}
        logoUrl={LOGO_URL}
        onPrivacyClick={() => setPrivacyOpen(true)}
      />
      <PrivacyPolicy open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}
